import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/client';
import MetricCard from '../components/MetricCard';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

function resolvePreviewVar(descriptor) {
  if (!descriptor) return '___';
  if (descriptor.type === 'contact_field') {
    const labels = { nombre: 'Juan', telefono: '+5215512345678', email: 'juan@email.com' };
    return labels[descriptor.field] || descriptor.field;
  }
  if (descriptor.type === 'static') return descriptor.value || '___';
  return '___';
}

function getTemplateBodyText(metaTemplate) {
  if (!metaTemplate) return '';
  const body = (metaTemplate.components || []).find((c) => c.type === 'BODY');
  return body?.text || '';
}

function getTemplateButtonText(metaTemplate) {
  if (!metaTemplate) return null;
  const buttons = (metaTemplate.components || []).find((c) => c.type === 'BUTTONS');
  const urlBtn = buttons?.buttons?.find((b) => b.type === 'URL');
  return urlBtn?.text || null;
}

export default function CampaignDashboard() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metaTemplate, setMetaTemplate] = useState(null);
  const [templateStatus, setTemplateStatus] = useState('loading'); // loading | found | not_found | error
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [clickFilter, setClickFilter] = useState('all'); // all | yes | no
  const perPage = 50;

  useEffect(() => {
    Promise.all([
      api.get(`/dashboard/${id}`),
      api.get(`/campaigns/${id}`),
    ]).then(([dashRes, campRes]) => {
      setData(dashRes.data);
      setCampaign(campRes.data);
      // Try to fetch the matching Meta template so we can show how the message looks.
      // We ask for ALL statuses (not only APPROVED) because the template may have been
      // paused or rejected after the campaign was sent — we still want to render it.
      if (campRes.data?.template_name) {
        api.get('/whatsapp/templates?status=all').then((tplRes) => {
          const list = tplRes.data || [];
          const match = list.find((t) =>
            t.name === campRes.data.template_name
            && (!campRes.data.template_language || t.language === campRes.data.template_language)
          ) || list.find((t) => t.name === campRes.data.template_name);
          if (match) {
            setMetaTemplate(match);
            setTemplateStatus('found');
          } else {
            setTemplateStatus('not_found');
          }
        }).catch(() => setTemplateStatus('error'));
      } else {
        setTemplateStatus('not_found');
      }
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div>Cargando...</div>;
  if (!data) return <div>Error</div>;

  const chartData = {
    labels: ['Leidos', 'Entregados (no leidos)', 'Fallidos'],
    datasets: [{
      data: [data.read, data.delivered - data.read, data.failed],
      backgroundColor: ['#4ecca3', '#3498db', '#e74c3c'],
    }],
  };

  const handleSend = async () => {
    if (!window.confirm('Enviar esta campana ahora?')) return;
    try {
      const { data: res } = await api.post(`/campaigns/${id}/send`);
      alert(res.message);
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Error');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Detener esta campaña? Los mensajes ya enviados no se pueden recuperar — solo se cancelarán los pendientes.')) return;
    try {
      const { data: res } = await api.post(`/campaigns/${id}/cancel`);
      alert(res.message);
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Error');
    }
  };

  return (
    <div>
      <Link to="/campaigns" style={{ color: '#4ecca3', fontSize: 13 }}>Volver a campanas</Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
        <h1 style={{ margin: 0 }}>{campaign?.nombre}</h1>
        {campaign?.estado === 'borrador' && (
          <button onClick={handleSend} style={{
            padding: '8px 20px', background: '#2ecc71', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
          }}>Enviar ahora</button>
        )}
        {(campaign?.estado === 'enviando' || campaign?.estado === 'programada') && (
          <button onClick={handleCancel} style={{
            padding: '8px 20px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600,
          }}>⏹ Detener envío</button>
        )}
      </div>
      <p style={{ color: '#888', fontSize: 13 }}>Tipo: {campaign?.tipo} | Estado: {campaign?.estado}</p>

      {campaign && (
        <div style={{ display: 'flex', gap: 16, marginTop: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ background: '#fff', borderRadius: 8, padding: 20, display: 'flex', gap: 20, alignItems: 'flex-start', flex: 1, minWidth: 360 }}>
          {campaign.media_url && campaign.media_type === 'image' && (
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Imagen enviada</div>
              <a href={campaign.media_url} target="_blank" rel="noreferrer">
                <img
                  src={campaign.media_url}
                  alt="Imagen de la campaña"
                  style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee', display: 'block' }}
                  onError={(e) => { e.target.style.opacity = 0.3; }}
                />
              </a>
              <div style={{ fontSize: 10, color: '#aaa', marginTop: 4, maxWidth: 180, wordBreak: 'break-all' }}>
                <span
                  title="Clic para copiar URL"
                  onClick={() => navigator.clipboard.writeText(campaign.media_url)}
                  style={{ cursor: 'pointer' }}
                >{campaign.media_url}</span>
              </div>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Template</div>
            <div style={{ fontSize: 14, fontFamily: 'monospace', marginBottom: 12 }}>
              {campaign.template_name || '—'} <span style={{ color: '#888', fontSize: 11 }}>({campaign.template_language || 'es_MX'})</span>
            </div>

            {Array.isArray(campaign.template_variables) && campaign.template_variables.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Variables del cuerpo</div>
                <table style={{ fontSize: 12, marginBottom: 12, borderCollapse: 'collapse' }}>
                  <tbody>
                    {campaign.template_variables.map((v, i) => (
                      <tr key={i}>
                        <td style={{ padding: '2px 8px 2px 0', color: '#888', fontFamily: 'monospace' }}>{`{{${i + 1}}}`}</td>
                        <td style={{ padding: '2px 0' }}>
                          {v?.type === 'contact_field' ? (
                            <span style={{ color: '#3498db' }}>contacto.{v.field}</span>
                          ) : (
                            <span>{v?.value || <em style={{ color: '#ccc' }}>vacío</em>}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {campaign.button_url_variable && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Variable del botón</div>
                <div style={{ fontSize: 12, marginBottom: 12 }}>
                  {campaign.button_url_variable.type === 'contact_field' ? (
                    <span style={{ color: '#3498db' }}>contacto.{campaign.button_url_variable.field}</span>
                  ) : (
                    <span style={{ fontFamily: 'monospace' }}>{campaign.button_url_variable.value || <em style={{ color: '#ccc' }}>vacío</em>}</span>
                  )}
                </div>
              </>
            )}

            {campaign.link && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Link de tracking</div>
                <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#555', wordBreak: 'break-all' }}>{campaign.link}</div>
              </>
            )}
          </div>
        </div>

        {(() => {
          const bodyText = getTemplateBodyText(metaTemplate);
          const buttonText = getTemplateButtonText(metaTemplate);
          let previewText = bodyText;
          (campaign.template_variables || []).forEach((v, i) => {
            previewText = previewText.replace(`{{${i + 1}}}`, resolvePreviewVar(v));
          });
          const hasHeaderImage = campaign.media_type === 'image' && !!campaign.media_url;
          return (
            <div style={{ width: 320, flexShrink: 0, background: '#fff', borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Vista previa en WhatsApp
              </div>
              <div style={{
                background: '#e5ddd5',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'400\' height=\'400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'60\' height=\'60\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M30 5 L35 15 L30 25 L25 15Z\' fill=\'%23d4ccb5\' opacity=\'0.15\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect fill=\'url(%23p)\' width=\'400\' height=\'400\'/%3E%3C/svg%3E")',
                borderRadius: 12,
                padding: 16,
                minHeight: 280,
                display: 'flex',
                justifyContent: 'flex-end',
                flexDirection: 'column',
              }}>
                <div style={{
                  background: '#dcf8c6',
                  borderRadius: '8px 0 8px 8px',
                  maxWidth: 270,
                  alignSelf: 'flex-end',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                  overflow: 'hidden',
                }}>
                  {hasHeaderImage && (
                    <div style={{ width: '100%', height: 150, background: '#c8c8c8', overflow: 'hidden' }}>
                      <img
                        src={campaign.media_url}
                        alt="Header"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div style={{ padding: '8px 12px 4px' }}>
                    {previewText ? (
                      <div style={{ fontSize: 13, lineHeight: 1.4, color: '#303030', whiteSpace: 'pre-wrap' }}
                        dangerouslySetInnerHTML={{ __html: previewText.replace(/\*([^*]+)\*/g, '<b>$1</b>') }}
                      />
                    ) : (
                      <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic', lineHeight: 1.4 }}>
                        {templateStatus === 'loading' && 'Cargando plantilla de Meta…'}
                        {templateStatus === 'not_found' && (
                          <>
                            Plantilla <b style={{ fontFamily: 'monospace', fontStyle: 'normal' }}>{campaign.template_name}</b> no se encontró en Meta (puede haber sido renombrada o eliminada).
                            <br />Variables enviadas:
                            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                              {(campaign.template_variables || []).map((v, i) => (
                                <li key={i} style={{ fontSize: 11 }}>
                                  {`{{${i + 1}}}`} → {v?.type === 'contact_field' ? `contacto.${v.field}` : (v?.value || 'vacío')}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {templateStatus === 'error' && 'Error al cargar plantilla de Meta. Verifica conexión.'}
                        {templateStatus === 'found' && '(plantilla sin cuerpo)'}
                      </div>
                    )}
                    {(previewText || templateStatus === 'found') && (
                      <div style={{ fontSize: 11, color: '#999', textAlign: 'right', marginTop: 4 }}>
                        {campaign.sent_at ? new Date(campaign.sent_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                      </div>
                    )}
                  </div>
                  {buttonText && (
                    <div style={{
                      borderTop: '1px solid #c8e6c0',
                      padding: '10px 12px',
                      textAlign: 'center',
                      fontSize: 13,
                      color: '#53bdeb',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}>
                      <span style={{ fontSize: 14 }}>&#x1F517;</span>
                      {buttonText}
                    </div>
                  )}
                </div>
                <div style={{
                  background: '#dcf8c6',
                  borderRadius: '8px 0 8px 8px',
                  maxWidth: 270,
                  alignSelf: 'flex-end',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                  marginTop: 4,
                  padding: '6px 12px',
                }}>
                  <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>
                    Responde PARAR para no recibir mas mensajes
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, margin: '20px 0' }}>
        <MetricCard title="Enviados" value={data.sent} />
        <MetricCard title="Entregados" value={data.delivered} />
        <MetricCard title="Leidos" value={data.read} subtitle={`Open rate: ${data.openRate}%`} />
        <MetricCard title="Clics" value={data.clicked} subtitle={`Click rate: ${data.clickRate}%`} color="#f39c12" />
        <MetricCard title="Fallidos" value={data.failed} color="#e74c3c" />
        <MetricCard title="Revenue" value={`$${Number(data.revenue).toLocaleString()}`} color="#2ecc71" />
        <MetricCard title="Tasa de respuesta" value={`${data.responseRate}%`} color="#3498db" />
      </div>

      {/* Failure reasons breakdown */}
      {data.failed > 0 && (() => {
        const failed = (data.messages || []).filter((m) => m.estado === 'fallido');
        const buckets = { no_whatsapp: 0, ecosystem: 0, otros: 0 };
        const otrosDetail = {};
        for (const m of failed) {
          const err = (m.error_message || '').toLowerCase();
          if (err.includes('undeliverable')) buckets.no_whatsapp++;
          else if (err.includes('ecosystem') || err.includes('engagement')) buckets.ecosystem++;
          else {
            buckets.otros++;
            const key = m.error_message || '(sin detalle)';
            otrosDetail[key] = (otrosDetail[key] || 0) + 1;
          }
        }
        const total = failed.length;
        const pct = (n) => total > 0 ? ((n / total) * 100).toFixed(0) : 0;
        return (
          <div style={{ background: '#fff', borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 14px' }}>Razones de fallo <span style={{ fontSize: 13, color: '#888', fontWeight: 400 }}>({total} totales)</span></h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <div style={{ background: '#f8d7da', borderRadius: 6, padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#721c24', textTransform: 'uppercase', letterSpacing: 0.5 }}>Sin WhatsApp</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#721c24', marginTop: 4 }}>{buckets.no_whatsapp} <span style={{ fontSize: 13, fontWeight: 400 }}>({pct(buckets.no_whatsapp)}%)</span></div>
                <div style={{ fontSize: 11, color: '#721c24', marginTop: 4 }}>Números fijos o sin cuenta WA — auto-excluidos en futuras campañas</div>
              </div>
              <div style={{ background: '#fff3cd', borderRadius: 6, padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#856404', textTransform: 'uppercase', letterSpacing: 0.5 }}>Filtrado por Meta</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#856404', marginTop: 4 }}>{buckets.ecosystem} <span style={{ fontSize: 13, fontWeight: 400 }}>({pct(buckets.ecosystem)}%)</span></div>
                <div style={{ fontSize: 11, color: '#856404', marginTop: 4 }}>"healthy ecosystem engagement" — Meta protege al destinatario, puede cambiar</div>
              </div>
              <div style={{ background: '#e2e3e5', borderRadius: 6, padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#383d41', textTransform: 'uppercase', letterSpacing: 0.5 }}>Otros</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#383d41', marginTop: 4 }}>{buckets.otros} <span style={{ fontSize: 13, fontWeight: 400 }}>({pct(buckets.otros)}%)</span></div>
                {Object.keys(otrosDetail).length > 0 && (
                  <div style={{ fontSize: 10, color: '#383d41', marginTop: 4, maxHeight: 60, overflow: 'auto' }}>
                    {Object.entries(otrosDetail).map(([msg, count]) => (
                      <div key={msg}>{count}× {msg}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {buckets.no_whatsapp > 0 && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                💡 Los {buckets.no_whatsapp} sin WhatsApp ya quedaron marcados en /contacts y no se les volverá a mandar en futuras campañas.
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20 }}>
        <div style={{ background: '#fff', borderRadius: 8, padding: 24 }}>
          <h3 style={{ marginTop: 0 }}>Distribucion</h3>
          <Doughnut data={chartData} />
        </div>

        <div style={{ background: '#fff', borderRadius: 8, padding: 24, overflowX: 'auto' }}>
          {(() => {
            const allMessages = data.messages || [];
            const lowSearch = search.trim().toLowerCase();
            const filtered = allMessages.filter((m) => {
              if (statusFilter !== 'all' && m.estado !== statusFilter) return false;
              if (clickFilter === 'yes' && !m.clicked) return false;
              if (clickFilter === 'no' && m.clicked) return false;
              if (lowSearch) {
                const blob = `${m.nombre || ''} ${m.telefono || ''} ${m.contact_id} ${m.whatsapp_message_id || ''} ${m.error_message || ''}`.toLowerCase();
                if (!blob.includes(lowSearch)) return false;
              }
              return true;
            });
            const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
            const safePage = Math.min(page, totalPages);
            const start = (safePage - 1) * perPage;
            const visible = filtered.slice(start, start + perPage);

            const statusCounts = allMessages.reduce((acc, m) => {
              acc[m.estado] = (acc[m.estado] || 0) + 1;
              return acc;
            }, {});

            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                  <h3 style={{ margin: 0 }}>Detalle por contacto <span style={{ color: '#888', fontWeight: 400, fontSize: 13 }}>({filtered.length.toLocaleString()} de {allMessages.length.toLocaleString()})</span></h3>
                  <button
                    type="button"
                    onClick={async () => {
                      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
                      try {
                        const res = await api.get(`/dashboard/${id}/export${params}`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement('a');
                        a.href = url;
                        const cd = res.headers['content-disposition'] || '';
                        const match = cd.match(/filename="?([^"]+)"?/);
                        a.download = match ? match[1] : `campana_${id}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        alert(err.response?.data?.error || 'Error al exportar');
                      }
                    }}
                    title={statusFilter === 'all'
                      ? 'Exporta todos los contactos de esta campaña'
                      : `Exporta solo los contactos con estado "${statusFilter}" (útil para crear nueva lista con ellos)`}
                    style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 12 }}
                  >📥 Exportar {statusFilter === 'all' ? 'todos' : statusFilter} a Excel</button>
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    placeholder="Buscar por nombre, teléfono, ID, error o WhatsApp ID…"
                    style={{ flex: 1, minWidth: 220, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
                  />
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
                  >
                    <option value="all">Todos los estados ({allMessages.length})</option>
                    {['enviado', 'entregado', 'leido', 'fallido', 'pendiente', 'cancelado'].map((s) =>
                      statusCounts[s] ? (
                        <option key={s} value={s}>{s} ({statusCounts[s]})</option>
                      ) : null
                    )}
                  </select>
                  <select
                    value={clickFilter}
                    onChange={(e) => { setClickFilter(e.target.value); setPage(1); }}
                    style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
                  >
                    <option value="all">Clic: todos</option>
                    <option value="yes">Clic: sí</option>
                    <option value="no">Clic: no</option>
                  </select>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                      <th style={{ padding: 8 }}>ID</th>
                      <th>Nombre</th>
                      <th>Teléfono</th>
                      <th>Estado</th>
                      <th>Clic</th>
                      <th>Clics</th>
                      <th>Enviado</th>
                      <th>WhatsApp ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((m) => (
                      <tr key={m.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: 8, color: '#888' }}>{m.contact_id}</td>
                        <td>{m.nombre || <span style={{ color: '#ccc' }}>—</span>}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{m.telefono || <span style={{ color: '#ccc' }}>—</span>}</td>
                        <td>
                          {m.estado}
                          {m.estado === 'fallido' && m.error_message && (
                            <div style={{ color: '#e74c3c', fontSize: 10, marginTop: 2, maxWidth: 400, wordBreak: 'break-word' }}>
                              {m.error_message}
                            </div>
                          )}
                        </td>
                        <td>{m.clicked ? 'Sí' : '-'}</td>
                        <td>{m.click_count}</td>
                        <td>{m.sent_at ? new Date(m.sent_at).toLocaleString() : '-'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 10, color: '#888', maxWidth: 240, wordBreak: 'break-all' }}>
                          {m.whatsapp_message_id ? (
                            <span
                              title="Clic para copiar"
                              onClick={() => navigator.clipboard.writeText(m.whatsapp_message_id)}
                              style={{ cursor: 'pointer' }}
                            >{m.whatsapp_message_id}</span>
                          ) : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                      </tr>
                    ))}
                    {visible.length === 0 && (
                      <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#888' }}>Sin resultados para los filtros actuales</td></tr>
                    )}
                  </tbody>
                </table>

                {filtered.length > perPage && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 14, fontSize: 12 }}>
                    <button
                      disabled={safePage <= 1}
                      onClick={() => setPage(safePage - 1)}
                      style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: safePage <= 1 ? 'not-allowed' : 'pointer' }}
                    >Anterior</button>
                    <span style={{ color: '#666' }}>Página {safePage} de {totalPages}</span>
                    <button
                      disabled={safePage >= totalPages}
                      onClick={() => setPage(safePage + 1)}
                      style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: safePage >= totalPages ? 'not-allowed' : 'pointer' }}
                    >Siguiente</button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
